import { useCallback, useMemo, useRef, useState } from "react";
import { Flex, Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  Alert,
  Button,
  Dialog,
  EmptyState,
  Input,
  Select,
  Table,
  Tbody,
  Td,
  Textarea,
  Th,
  Thead,
  Tr,
  useToast,
} from "@/components/ui";
import { apiErrorMessage, newIdempotencyKey } from "@/api/client";
import { useApiClient } from "@/api/useApiClient";
import { LoadBoundary, Panel, TwoCol, useHubLoad } from "@/modules/hub-kit/HubKit";
import { formatCount, formatDateTime } from "@/modules/hub-kit/format";
import { createGrowthApi, SEGMENTS, type SegmentKey, type SegmentSize } from "../growthApi";

const HREF_RE = /^\/[A-Za-z0-9_/\-?=&.]*$/;

function segmentLabel(key: string): string {
  return SEGMENTS.find((s) => s.key === key)?.label ?? (key === "audience" ? "HQ audience" : key);
}

/**
 * An announcement into Play users' in-app inbox, to a segment. It lands in
 * the `notifications` table Play already reads (category "promotions"), so
 * it shows in the inbox immediately. Players who switched promotions off are
 * skipped. No push, email or SMS: those need a vendor that is not chosen.
 */
export function BroadcastsTab() {
  const client = useApiClient();
  const api = useMemo(() => createGrowthApi(client), [client]);
  const toast = useToast();

  const load = useCallback(async () => (await api.listBroadcasts()).broadcasts, [api]);
  const history = useHubLoad(load);

  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [href, setHref] = useState("");
  const [segment, setSegment] = useState<SegmentKey>("all_players");
  const [audienceId, setAudienceId] = useState("");
  const [preview, setPreview] = useState<SegmentSize | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  // One key per confirmed send, so a retry after a network error cannot double-send.
  const keyRef = useRef<string>(newIdempotencyKey());

  const formError =
    !title.trim()
      ? "Add a title."
      : title.trim().length > 80
        ? "Keep the title to 80 characters."
        : body.length > 500
          ? "Keep the message to 500 characters."
          : href && !HREF_RE.test(href.trim())
            ? "The link must be an in-app path such as /inbox, not a web address."
            : segment === "audience" && !/^[0-9a-f-]{36}$/i.test(audienceId.trim())
              ? "Enter the audience id."
              : null;

  function resetPreview() {
    setPreview(null);
    setPreviewError(null);
  }

  async function runPreview() {
    setPreviewing(true);
    setPreviewError(null);
    try {
      const r = await api.segmentSize(segment, segment === "audience" ? audienceId.trim() : undefined);
      setPreview(r.size);
    } catch (e) {
      setPreviewError(apiErrorMessage(e));
    } finally {
      setPreviewing(false);
    }
  }

  async function send() {
    if (!preview) return;
    setSending(true);
    setSendError(null);
    try {
      const r = await api.sendBroadcast(
        {
          title: title.trim(),
          ...(body.trim() ? { body: body.trim() } : {}),
          href: href.trim() || null,
          segment,
          ...(segment === "audience" ? { audience_id: audienceId.trim() } : {}),
          confirm_recipient_count: preview.eligible,
        },
        keyRef.current,
      );
      toast.push({
        title: r.replay ? "Already sent" : `Sent to ${formatCount(r.recipient_count)} inbox${r.recipient_count === 1 ? "" : "es"}`,
        tone: "success",
      });
      keyRef.current = newIdempotencyKey();
      setConfirmOpen(false);
      setTyped("");
      setTitle("");
      setBody("");
      setHref("");
      resetPreview();
      void history.reload();
    } catch (e) {
      setSendError(apiErrorMessage(e));
      // The audience moved: make the operator look at the new number.
      if (/audience changed/i.test(apiErrorMessage(e))) resetPreview();
    } finally {
      setSending(false);
    }
  }

  return (
    <Stack gap={24}>
      <TwoCol>
        <Panel
          title="New in-app announcement"
          description="Delivered to the Play inbox of everyone in the segment, under Promotions. Players who turned promotions off are skipped."
        >
          <Stack gap={12}>
            <Input label="Title" name="bc-title" value={title} maxLength={80} onChange={(e) => { setTitle(e.target.value); }} placeholder="Weekend courts are open" />
            <Textarea label="Message" name="bc-body" value={body} maxLength={500} onChange={(e) => setBody(e.target.value)} rows={4} />
            <Input
              label="Opens in the app (optional)"
              name="bc-href"
              value={href}
              onChange={(e) => setHref(e.target.value)}
              placeholder="/inbox"
              hint="An in-app path. Web links are refused."
            />
            <Select
              label="Send to"
              name="bc-segment"
              value={segment}
              onChange={(e) => {
                setSegment(e.target.value as SegmentKey);
                resetPreview();
              }}
              options={[
                ...SEGMENTS.map((s) => ({ value: s.key, label: s.label })),
                { value: "audience", label: "An approved HQ audience" },
              ]}
            />
            {segment === "audience" ? (
              <Input
                label="Audience id"
                name="bc-audience"
                value={audienceId}
                onChange={(e) => {
                  setAudienceId(e.target.value);
                  resetPreview();
                }}
                hint="From Segments, HQ audiences. Only approved or active audiences, and only their active members."
              />
            ) : (
              <Text role="small" color="secondary" as="p">
                {SEGMENTS.find((s) => s.key === segment)?.description}
              </Text>
            )}
            {formError ? (
              <Text role="small" as="p" style={{ color: "var(--color-danger)" }}>
                {formError}
              </Text>
            ) : null}
            <Flex gap={8} wrap>
              <Button variant="secondary" onClick={() => void runPreview()} loading={previewing} disabled={!!formError}>
                Count recipients
              </Button>
              <Button
                variant="primary"
                disabled={!!formError || !preview || preview.eligible === 0}
                onClick={() => {
                  setTyped("");
                  setSendError(null);
                  setConfirmOpen(true);
                }}
              >
                Review and send
              </Button>
            </Flex>
            {previewError ? (
              <Alert tone="danger" title="Could not count the segment">
                {previewError}
              </Alert>
            ) : null}
            {preview ? (
              <Alert tone={preview.eligible === 0 ? "warning" : "info"} title={`${formatCount(preview.eligible)} will receive it`}>
                {formatCount(preview.in_segment)} in the segment, {formatCount(preview.opted_out)} opted out of promotions.
              </Alert>
            ) : null}
          </Stack>
        </Panel>

        <Panel title="How it looks in Play" description="Inbox row, Promotions category.">
          <div
            style={{
              border: "1px solid var(--color-border-subtle)",
              borderRadius: "var(--radius-md)",
              padding: "var(--space-12)",
              background: "var(--color-surface-canvas)",
            }}
          >
            <Text role="bodyStrong" as="p">
              {title.trim() || "Title"}
            </Text>
            <Text role="small" color="secondary" as="p">
              {body.trim() || "Message"}
            </Text>
            {href.trim() ? (
              <Text role="caption" color="muted" as="p">
                Tapping opens {href.trim()}
              </Text>
            ) : null}
          </div>
          <Text role="caption" color="muted" as="p">
            In-app only. Push, email and SMS are not connected: they need a delivery provider, and none has been chosen.
          </Text>
        </Panel>
      </TwoCol>

      <Panel title="Sent announcements" description="Every broadcast, who it reached and how many were skipped.">
        <LoadBoundary loading={history.loading} error={history.error} onRetry={() => void history.reload()} hasData={!!history.data} what="sent announcements">
          {history.data && history.data.length === 0 ? (
            <EmptyState title="Nothing sent yet" description="Write an announcement above, count its recipients, then review and send." />
          ) : null}
          {history.data && history.data.length > 0 ? (
            <Table>
              <Thead>
                <Tr>
                  <Th>Sent</Th>
                  <Th>Title</Th>
                  <Th>Segment</Th>
                  <Th>Delivered to</Th>
                  <Th>Opted out</Th>
                </Tr>
              </Thead>
              <Tbody>
                {history.data.map((b) => (
                  <Tr key={b.id}>
                    <Td>{formatDateTime(b.created_at)}</Td>
                    <Td>{b.title}</Td>
                    <Td>{segmentLabel(b.segment_key)}</Td>
                    <Td>{formatCount(b.recipient_count)}</Td>
                    <Td>{formatCount(b.skipped_opted_out)}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          ) : null}
        </LoadBoundary>
      </Panel>

      <Dialog
        open={confirmOpen}
        onClose={() => {
          if (!sending) setConfirmOpen(false);
        }}
        title="Send this announcement?"
        actions={
          <Flex gap={8} justify="flex-end">
            <Button variant="secondary" onClick={() => setConfirmOpen(false)} disabled={sending}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={sending}
              disabled={!preview || typed.trim() !== String(preview.eligible)}
              onClick={() => void send()}
            >
              Send to {preview ? formatCount(preview.eligible) : 0}
            </Button>
          </Flex>
        }
      >
        <Stack gap={12}>
          <Text role="body" as="p">
            "{title.trim()}" goes to {preview ? formatCount(preview.eligible) : 0} Play inboxes ({segmentLabel(segment)}). It cannot be unsent.
          </Text>
          <Input
            label={`Type ${preview?.eligible ?? 0} to confirm`}
            name="bc-confirm"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            inputMode="numeric"
          />
          {sendError ? (
            <Alert tone="danger" title="Not sent">
              {sendError}
            </Alert>
          ) : null}
        </Stack>
      </Dialog>
    </Stack>
  );
}
